import { Period, PERIODS } from "@/shared/domain/metrics";

export const TIME_RANGES = [
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "90d", label: "Last 90 days", days: 90 },
  { value: "365d", label: "Last year", days: 365 },
  { value: "all", label: "All time", days: 3650 },
] as const;

export type TimeRange = (typeof TIME_RANGES)[number]["value"];

/**
 * Resolve a `TimeRange` value to a Unix-epoch nanosecond pair anchored at
 * `now`. The `end` is `now` so future-dated sessions don't accidentally creep
 * in; the `start` is `now - days(range)`. The backend aggregate endpoint
 * expects nanosecond ints (see `AggregateMetricsRequest.start_time_ns`).
 */
export function timeRangeToNsBounds(
  range: TimeRange,
  now: Date = new Date(),
): { start_time_ns: number; end_time_ns: number } {
  const cfg = TIME_RANGES.find((tr) => tr.value === range) ?? TIME_RANGES[0];
  const startMs = now.getTime() - cfg.days * 24 * 60 * 60 * 1000;
  return {
    start_time_ns: startMs * 1_000_000,
    end_time_ns: now.getTime() * 1_000_000,
  };
}

// Both axes are ordered coarsest-last. The canonical pairing is "one step
// coarser than the range index", i.e. the coarsest period that still yields a
// useful number of buckets for the range:
//
//   30d ↔ weekly   90d ↔ monthly   365d ↔ quarterly   all ↔ yearly
//
// A pair "fits" when the period is no coarser than that canonical maximum.
const RANGE_ORDER: readonly TimeRange[] = TIME_RANGES.map((tr) => tr.value);

function rangeIndex(range: TimeRange): number {
  return RANGE_ORDER.indexOf(range);
}

function periodIndex(period: Period): number {
  return PERIODS.indexOf(period);
}

/**
 * Coarsest period that still fits `range` (the canonical pairing above). Used
 * to auto-fit the period when the current one is too fine-grained to remain.
 */
export function periodForRange(range: TimeRange): Period {
  const idx = Math.min(rangeIndex(range) + 1, PERIODS.length - 1);
  return PERIODS[idx];
}

/**
 * Shortest range that fits `period` (inverse of the canonical pairing). Used to
 * auto-expand the window when it's too short for the chosen period.
 */
export function rangeForPeriod(period: Period): TimeRange {
  const idx = Math.min(
    Math.max(periodIndex(period) - 1, 0),
    RANGE_ORDER.length - 1,
  );
  return RANGE_ORDER[idx];
}

/**
 * Whether `period` is appropriate for `range` — i.e. not so coarse that the
 * window holds too few buckets. The dashboard only auto-adjusts the
 * (range, period) pair when this is false, so a deliberately fine period over a
 * long window (more buckets) is left alone.
 */
export function periodFitsRange(range: TimeRange, period: Period): boolean {
  return periodIndex(period) <= rangeIndex(range) + 1;
}
